import { useCallback, useEffect, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { bucketModal } from "../api/plan";
import { generateTokenByApiKey } from "../api/login";

// Module-level cache: { "<tenantId>:<benefitId>": { status, price, error } }
// Lives outside the hook so price already fetched for a benefit id survives
// the panel being unmounted/remounted (e.g. switching tabs away and back) -
// re-selecting a tier the user already picked before never re-hits the API.
const priceCache = new Map();

const STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
};

// How the price is read out of a bucketModal response.
const extractPrice = (res) => Number(res?.benefit?.benefitAmount ?? 0);

// Requests still awaiting a response after this long are assumed to have
// failed silently (the underlying action only invokes its callback on a 200,
// so a non-200/network hiccup would otherwise leave the entry loading forever).
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Fetches and caches per-benefit pricing from the bucket-model API, keyed by
 * benefit id. Call `requestPrice(id)` whenever a benefit id becomes the
 * user's active selection (slider move, tier button click, category
 * toggle-on); it is a no-op if that id is already cached or already
 * in-flight, so repeated selection/deselection of the same tier never
 * triggers duplicate network calls.
 */
const useBucketPricing = (tenantId) => {
  const dispatch = useDispatch();
  const [, forceRender] = useReducer((n) => n + 1, 0);

  const tokenRef = useRef(null);
  const tokenPromiseRef = useRef(null);
  const timersRef = useRef(new Map()); // benefitId -> timeout handle

  // Access token is fetched once per mount and reused for every benefit id -
  // no point re-authenticating per price lookup.
  const getAccessToken = useCallback(() => {
    if (tokenRef.current) return Promise.resolve(tokenRef.current);
    if (tokenPromiseRef.current) return tokenPromiseRef.current;

    let keys;
    try {
      keys = JSON.parse(process.env.REACT_APP_API_KEYS || "[]");
    } catch {
      keys = [];
    }
    const key = keys.find((f) => f.tenantId === tenantId);
    if (!key) return Promise.resolve(null);

    tokenPromiseRef.current = new Promise((resolve) => {
      dispatch(
        generateTokenByApiKey(
          {},
          "zealthix",
          {
            "x-tenant-id": key.tenantId,
            "X-Api-Key": key.key,
            "Content-Type": "application/json",
          },
          (t) => {
            tokenRef.current = t?.access_token || null;
            tokenPromiseRef.current = null;
            resolve(tokenRef.current);
          }
        )
      );
    });
    return tokenPromiseRef.current;
  }, [dispatch, tenantId]);

  const cacheKey = useCallback((id) => `${tenantId}:${id}`, [tenantId]);

  const getPrice = useCallback(
    (benefitId) => (benefitId == null ? undefined : priceCache.get(cacheKey(benefitId))),
    [cacheKey]
  );

  const requestPrice = useCallback(
    async (benefitId) => {
      if (benefitId == null || !tenantId) return;
      const key = cacheKey(benefitId);
      const existing = priceCache.get(key);
      // Memoized: already have it, or already fetching it - skip.
      if (existing && (existing.status === STATUS.SUCCESS || existing.status === STATUS.LOADING)) {
        return;
      }

      priceCache.set(key, { status: STATUS.LOADING });
      forceRender();

      const token = await getAccessToken();
      if (!token) {
        priceCache.set(key, { status: STATUS.ERROR, error: "Could not authenticate." });
        forceRender();
        return;
      }

      const timer = setTimeout(() => {
        const current = priceCache.get(key);
        if (current?.status === STATUS.LOADING) {
          priceCache.set(key, { status: STATUS.ERROR, error: "Timed out fetching price." });
          forceRender();
        }
        timersRef.current.delete(benefitId);
      }, REQUEST_TIMEOUT_MS);
      timersRef.current.set(benefitId, timer);

      dispatch(
        bucketModal({ id: benefitId }, token, (res) => {
          const pendingTimer = timersRef.current.get(benefitId);
          if (pendingTimer) {
            clearTimeout(pendingTimer);
            timersRef.current.delete(benefitId);
          }
          priceCache.set(key, { status: STATUS.SUCCESS, price: extractPrice(res), raw: res });
          forceRender();
        })
      );
    },
    [tenantId, cacheKey, getAccessToken, dispatch]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  return { getPrice, requestPrice };
};

export default useBucketPricing;
